import React, { useMemo } from "react"
import { startOfDay } from "date-fns"
import { ConsumptionPoint } from "../../types/suggestedFulfilments"

interface ConsumptionLineChartProps {
  points: ConsumptionPoint[]
  width: number
  height: number
  visibleTimeStart: number
  visibleTimeEnd: number
  /** Highlight empty (level=0) periods with an orange line. Default true. */
  showEmptyHighlight?: boolean
}

export const ConsumptionLineChart: React.FC<ConsumptionLineChartProps> = ({
  points,
  width,
  height,
  visibleTimeStart,
  visibleTimeEnd,
  showEmptyHighlight = true,
}) => {
  const { historicalPath, forecastPath, emptyZoneLines, zeroY } = useMemo(() => {
    if (points.length === 0) {
      return { historicalPath: "", forecastPath: "", emptyZoneLines: [] as { x1: number; x2: number }[], zeroY: 0 }
    }

    const padding = 4
    const chartHeight = height - padding * 2

    // Filter points to visible time range that have level data.
    // Include the last point BEFORE the visible window so the line
    // enters from the left edge instead of starting abruptly.
    const pointsWithLevel = points.filter(
      (p) => p.level !== undefined && p.level !== null
    )

    // Find the last point before the visible window to use as an anchor
    let anchorPoint: ConsumptionPoint | null = null
    for (const p of pointsWithLevel) {
      const t = new Date(p.date).getTime()
      if (t < visibleTimeStart) {
        anchorPoint = p
      } else {
        break
      }
    }

    const visiblePoints = pointsWithLevel.filter((p) => {
      const pointTime = new Date(p.date).getTime()
      return pointTime >= visibleTimeStart && pointTime <= visibleTimeEnd
    })

    // Prepend the anchor point so the line extends from before the visible window
    if (anchorPoint && (visiblePoints.length === 0 || visiblePoints[0] !== anchorPoint)) {
      visiblePoints.unshift(anchorPoint)
    }

    if (visiblePoints.length === 0) {
      return { historicalPath: "", forecastPath: "", emptyZoneLines: [] as { x1: number; x2: number }[], zeroY: 0 }
    }

    // Split into historical and forecast
    const historicalPoints = visiblePoints.filter((p) => !p.is_forecast)

    // Find the most recent historical point
    const mostRecentHistoricalPoint = historicalPoints.length > 0
      ? historicalPoints.reduce((latest, p) =>
        new Date(p.date).getTime() > new Date(latest.date).getTime() ? p : latest
      )
      : null

    const mostRecentHistoricalTime = mostRecentHistoricalPoint
      ? new Date(mostRecentHistoricalPoint.date).getTime()
      : -Infinity

    // Only include forecast points that are after the most recent historical point
    // Prepend the most recent historical point to connect the lines
    const forecastPointsAfterHistorical = visiblePoints.filter((p) =>
      p.is_forecast && new Date(p.date).getTime() > mostRecentHistoricalTime
    )

    const forecastPoints = mostRecentHistoricalPoint && forecastPointsAfterHistorical.length > 0
      ? [mostRecentHistoricalPoint, ...forecastPointsAfterHistorical]
      : forecastPointsAfterHistorical

    // Clamp the Y-axis to the highest visible value (with 10% headroom)
    // so a single high reading doesn't squash the rest of the graph.
    const maxLevel = Math.max(
      ...visiblePoints.map((p) => p.level ?? 0),
      1 // avoid division by zero
    ) * 1.1

    // Helper to convert point to SVG coordinates
    const pointToCoords = (p: ConsumptionPoint): { x: number; y: number } => {
      // Normalize to start of day, then shift +1 day for end-of-day level display
      const pointDate = startOfDay(new Date(p.date))
      const pointTime = pointDate.getTime() + 86400000  // +1 day in ms
      const x = ((pointTime - visibleTimeStart) / (visibleTimeEnd - visibleTimeStart)) * width
      // Invert Y so 0 is at bottom and maxLevel is at top
      const y = padding + chartHeight - ((p.level ?? 0) / maxLevel) * chartHeight
      return { x, y }
    }

    // Build SVG path string
    const buildPath = (pts: ConsumptionPoint[]): string => {
      if (pts.length === 0) return ""

      const coords = pts.map(pointToCoords)
      const pathParts = coords.map((c, i) => {
        if (i === 0) return `M ${c.x},${c.y}`
        return `L ${c.x},${c.y}`
      })

      return pathParts.join(" ")
    }

    const historicalPath = buildPath(historicalPoints)
    const forecastPath = buildPath(forecastPoints)

    // Build orange line segments for days where the level is truly empty (level=0).
    // A day where fuel arrives (level=0 but next point's level > 0) is NOT empty —
    // that's a delivery day, so we exclude it.
    const emptyZoneLines: { x1: number; x2: number }[] = []

    if (showEmptyHighlight) {
      const sortedPoints = [...visiblePoints].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )

      for (let i = 0; i < sortedPoints.length; i++) {
        const level = sortedPoints[i].level ?? 0
        if (level !== 0) continue

        // Skip if the next point has level > 0 (fuel is arriving on this day)
        const nextLevel = i < sortedPoints.length - 1 ? (sortedPoints[i + 1].level ?? 0) : 0
        if (nextLevel > 0) continue

        const x1 = pointToCoords(sortedPoints[i]).x
        const x2 = i < sortedPoints.length - 1
          ? pointToCoords(sortedPoints[i + 1]).x
          : x1 + (width / ((visibleTimeEnd - visibleTimeStart) / 86400000)) // ~1 day in px

        if (x2 > x1) {
          // Merge with previous segment if adjacent
          const prev = emptyZoneLines[emptyZoneLines.length - 1]
          if (prev && Math.abs(prev.x2 - x1) < 1) {
            prev.x2 = x2
          } else {
            emptyZoneLines.push({ x1, x2 })
          }
        }
      }
    }

    // Calculate zero line Y position (0 level is at the bottom of the chart)
    const zeroY = padding + chartHeight

    return { historicalPath, forecastPath, emptyZoneLines, zeroY }
  }, [points, width, height, visibleTimeStart, visibleTimeEnd, showEmptyHighlight])

  // Don't render if no data
  if (points.length === 0) {
    return null
  }

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
      }}
    >
      {/* Clip path that excludes empty zones so historical/forecast lines don't draw over them */}
      {emptyZoneLines.length > 0 && (
        <defs>
          <clipPath id={`non-empty-clip-${visibleTimeStart}`}>
            {(() => {
              const sorted = [...emptyZoneLines].sort((a, b) => a.x1 - b.x1)
              const rects: React.ReactElement[] = []
              let cursor = 0
              sorted.forEach((seg, i) => {
                if (seg.x1 > cursor) {
                  rects.push(
                    <rect key={`clip-${i}`} x={cursor} y={0} width={seg.x1 - cursor} height={height} />
                  )
                }
                cursor = Math.max(cursor, seg.x2)
              })
              if (cursor < width) {
                rects.push(
                  <rect key="clip-end" x={cursor} y={0} width={width - cursor} height={height} />
                )
              }
              return rects
            })()}
          </clipPath>
        </defs>
      )}

      {/* Orange line segments at the zero-line where the tank is empty */}
      {emptyZoneLines.map((seg, i) => (
        <line
          key={`empty-${i}`}
          x1={seg.x1}
          y1={zeroY}
          x2={seg.x2}
          y2={zeroY}
          stroke="rgba(169, 169, 169, 0.5)"
          strokeWidth="2"
          fill="none"
          strokeDasharray="6 3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {/* Historical line (blue solid) — clipped to exclude empty zones */}
      {historicalPath && (
        <path
          d={historicalPath}
          stroke="rgba(57, 174, 210, 0.8)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          clipPath={emptyZoneLines.length > 0 ? `url(#non-empty-clip-${visibleTimeStart})` : undefined}
        />
      )}

      {/* Forecast line (purple dashed) — clipped to exclude empty zones */}
      {forecastPath && (
        <path
          d={forecastPath}
          stroke="rgba(161, 112, 217, 0.8)"
          strokeWidth="2"
          fill="none"
          strokeDasharray="6 3"
          strokeLinecap="round"
          strokeLinejoin="round"
          clipPath={emptyZoneLines.length > 0 ? `url(#non-empty-clip-${visibleTimeStart})` : undefined}
        />
      )}
    </svg>
  )
}
